import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, AlertCircle, CheckCircle, Clock, TrendingUp, Users, DollarSign, Percent, CreditCard, Mail, Phone, X, Receipt, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DueDates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all'); // all, overdue, due_soon, upcoming, partial, paid
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [subscriberBills, setSubscriberBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [showSubscriberDialog, setShowSubscriberDialog] = useState(false);

  useEffect(() => {
    fetchDueDates();
  }, []);

  const fetchDueDates = async () => {
    try {
      const response = await axios.get(`${API}/due-dates`);
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching due dates:', error);
      toast.error('Failed to load due dates');
      setLoading(false);
    }
  };

  const handleRowClick = async (item) => {
    setSelectedSubscriber(item);
    setShowSubscriberDialog(true);
    setLoadingBills(true);
    
    try {
      // Fetch all bills for this subscriber
      const response = await axios.get(`${API}/subscriber-bills/${item.subscriber_id}`);
      setSubscriberBills(response.data);
    } catch (error) {
      console.error('Error fetching subscriber bills:', error);
      toast.error('Failed to load bills');
      setSubscriberBills([]);
    } finally {
      setLoadingBills(false);
    }
  };

  const goToPayments = (billNumber) => {
    // Navigate to payments page with bill context
    navigate('/arbe/payments', { state: { billNumber, subscriberId: selectedSubscriber?.subscriber_id } });
    setShowSubscriberDialog(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700 border-green-200';
      case 'partial': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'overdue': return 'bg-red-100 text-red-700 border-red-200';
      case 'due_soon': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'upcoming': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle size={16} className="text-green-600" />;
      case 'partial': return <TrendingUp size={16} className="text-amber-600" />;
      case 'overdue': return <AlertCircle size={16} className="text-red-600" />;
      case 'due_soon': return <Clock size={16} className="text-orange-600" />;
      case 'upcoming': return <Calendar size={16} className="text-blue-600" />;
      default: return <Calendar size={16} className="text-slate-600" />;
    }
  };

  const filteredDueDates = data?.due_dates?.filter(d => {
    if (filter === 'all') return true;
    return d.status === filter;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="due-dates-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Calendar size={28} className="text-blue-600" />
          Due Dates - All Unpaid Bills
        </h1>
        <p className="text-slate-600 mt-1">
          Track all unpaid subscriber bills (current month: {data?.current_month}) • 
          <span className="font-medium text-slate-700 ml-1">{data?.summary?.total_bills} total bills</span>
        </p>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Users size={16} className="text-slate-500" />
              <span className="text-xs text-slate-500 font-medium">Subscribers</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{data.summary.total_subscribers}</p>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard size={16} className="text-slate-500" />
              <span className="text-xs text-slate-600 font-medium">Total Bills</span>
            </div>
            <p className="text-2xl font-bold text-slate-700">{data.summary.total_bills || data.summary.total_subscribers}</p>
          </div>
          
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-xs text-red-600 font-medium">Overdue</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{data.summary.overdue}</p>
          </div>
          
          <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={16} className="text-orange-500" />
              <span className="text-xs text-orange-600 font-medium">Due Soon</span>
            </div>
            <p className="text-2xl font-bold text-orange-700">{data.summary.due_soon}</p>
          </div>
          
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-amber-500" />
              <span className="text-xs text-amber-600 font-medium">Partial</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{data.summary.partial}</p>
          </div>
          
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle size={16} className="text-green-500" />
              <span className="text-xs text-green-600 font-medium">Paid</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{data.summary.paid}</p>
          </div>
          
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-1">
              <Percent size={16} className="text-blue-500" />
              <span className="text-xs text-blue-600 font-medium">Collected</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{data.summary.collection_rate}%</p>
          </div>
        </div>
      )}

      {/* Financial Summary */}
      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <p className="text-sm text-slate-500 mb-1">Expected This Month</p>
            <p className="text-xl font-bold text-slate-900">₱{data.summary.total_expected?.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <p className="text-sm text-slate-500 mb-1">Collected</p>
            <p className="text-xl font-bold text-green-600">₱{data.summary.total_collected?.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <p className="text-sm text-slate-500 mb-1">Pending Collection</p>
            <p className="text-xl font-bold text-red-600">₱{data.summary.total_pending?.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { value: 'all', label: 'All', count: data?.summary?.total_subscribers },
          { value: 'overdue', label: 'Overdue', count: data?.summary?.overdue },
          { value: 'due_soon', label: 'Due Soon', count: data?.summary?.due_soon },
          { value: 'upcoming', label: 'Upcoming', count: data?.summary?.upcoming },
          { value: 'partial', label: 'Partial', count: data?.summary?.partial },
          { value: 'paid', label: 'Paid', count: data?.summary?.paid },
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.value
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`filter-${f.value}`}
          >
            {f.label} ({f.count || 0})
          </button>
        ))}
      </div>

      {/* Due Dates List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="due-dates-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Subscriber</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Plan</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Bill Month</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Bill #</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Balance</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Contact</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDueDates.length > 0 ? (
                filteredDueDates.map((item, index) => (
                  <tr 
                    key={`${item.subscriber_id}-${item.billing_month || index}`} 
                    className="hover:bg-blue-50 transition-colors cursor-pointer" 
                    data-testid={`due-date-row-${item.subscriber_id}-${item.billing_month}`}
                    onClick={() => handleRowClick(item)}
                  >
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900 hover:text-blue-600">{item.subscriber_name}</p>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{item.plan}</td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${item.is_overdue ? 'text-red-600' : 'text-slate-700'}`}>
                        {item.billing_month_display || item.billing_month}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-slate-700 bg-slate-100 px-2 py-1 rounded">
                        {item.bill_number}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900">₱{item.monthly_amount?.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      {item.balance > 0 ? (
                        <span className="font-bold text-red-600">₱{item.balance?.toLocaleString()}</span>
                      ) : (
                        <span className="font-bold text-green-600">₱0</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                        {getStatusIcon(item.status)}
                        {item.status_label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {item.subscriber_mobile && (
                          <a href={`tel:${item.subscriber_mobile}`} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded" title={item.subscriber_mobile}>
                            <Phone size={16} />
                          </a>
                        )}
                        {item.subscriber_email && (
                          <a href={`mailto:${item.subscriber_email}`} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded" title={item.subscriber_email}>
                            <Mail size={16} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                    No subscribers found for this filter
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Subscriber Bills Dialog */}
      <Dialog open={showSubscriberDialog} onOpenChange={setShowSubscriberDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={20} className="text-blue-600" />
              {selectedSubscriber?.subscriber_name} - All Bills
            </DialogTitle>
          </DialogHeader>
          
          {selectedSubscriber && (
            <div className="space-y-4">
              {/* Subscriber Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedSubscriber.subscriber_name}</p>
                <p className="text-sm text-slate-600">{selectedSubscriber.plan} • ₱{selectedSubscriber.monthly_amount?.toLocaleString()}/month</p>
                {selectedSubscriber.subscriber_mobile && (
                  <p className="text-sm text-slate-500">{selectedSubscriber.subscriber_mobile}</p>
                )}
              </div>
              
              {/* Bills List */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Unpaid Bills (from Dec 2025)</h4>
                {loadingBills ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-slate-500">Loading bills...</span>
                  </div>
                ) : subscriberBills.length > 0 ? (
                  <div className="space-y-2">
                    {subscriberBills.filter(b => b.status !== 'paid').map((bill) => (
                      <div
                        key={bill.id}
                        className={`p-3 rounded-lg border ${
                          bill.status === 'partial' 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-mono text-sm font-semibold text-slate-800">{bill.bill_number}</p>
                            <p className="text-sm text-slate-600">{bill.billing_month_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-red-600">₱{bill.balance?.toLocaleString()}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              bill.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {bill.status === 'partial' ? 'PARTIAL' : 'UNPAID'}
                            </span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="mt-2 w-full bg-blue-600 hover:bg-blue-700"
                          onClick={() => goToPayments(bill.bill_number)}
                        >
                          <CreditCard size={14} className="mr-1" />
                          Pay This Bill
                        </Button>
                      </div>
                    ))}
                    
                    {/* Summary */}
                    <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-slate-700">Total Outstanding:</span>
                        <span className="text-lg font-bold text-red-600">
                          ₱{subscriberBills.filter(b => b.status !== 'paid').reduce((sum, b) => sum + (b.balance || 0), 0).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {subscriberBills.filter(b => b.status !== 'paid').length} unpaid bill(s)
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <CheckCircle size={32} className="mx-auto mb-2 text-green-500" />
                    <p>All bills are paid!</p>
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowSubscriberDialog(false)}
                >
                  Close
                </Button>
                <Button
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  onClick={() => {
                    navigate('/arbe/payments');
                    setShowSubscriberDialog(false);
                  }}
                >
                  <ExternalLink size={14} className="mr-1" />
                  Go to Payments
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DueDates;
